import SentryRow from "../../../../../../components/template/sentry-row.jsx";
import InputForm from "../../../../../../components/input/input-form.jsx";
import SelectComponent from "../../../../../../components/select/select-component.jsx";
import {IMAGE_OPTIONS_RULES, LaneTypeRules, laneTypesArr} from "../../../utils/lanes-form-utils.js";
import {useQuery, useQueryClient} from "@tanstack/react-query";
import smartcaptureService from "../../../../../../services/web/smartcapture-service.js";
import Loader from "../../../../../../components/template/loader.jsx";
import {useEffect, useMemo, useState} from "react";
import {imageOptions, ocrOptions} from "./lanes-form-ui.jsx";
import nocService from "../../../../../../services/web/noc-service.js";
import InputSwitch from "../../../../../../components/input/input-switch.jsx";
import Button from "../../../../../../components/button/button.jsx";
import {FaCopy, FaQuestion} from "react-icons/fa";
import {FaGears, FaRotateRight} from "react-icons/fa6";
import {getAssetPath} from "../../../../../../utils/get-asset-path.js";
import projectUtils from "../../../../../../utils/project-utils.js";
import {toast} from "react-toastify";
import lanesService from "../../../../../../services/web/lanes-service.js";
import {mergeLatestPassage} from "../../../utils/lanes-list-utils.js";
import {Card} from "../../../../../../components/card/card.jsx";
import {useFormContext} from "react-hook-form";
import FormField from "../../../../../../components/react-hook-form/form-field.jsx";
import {useLanesFormDefault} from "../../../hooks/use-form-default-options.jsx";
const API_URL = import.meta.env.VITE_API_URL;

const LaneForm = ({maintenanceGroup, dataInfo, forceInactiveOnCreate = false}) => {
  const { watch , setValue, getValues} = useFormContext();
  const { handleType } = useLanesFormDefault(setValue);
  const queryClient = useQueryClient();
  const [isRefreshingLastPassage, setIsRefreshingLastPassage] = useState(false);

  const code = watch("lane.code");
  const status = watch("lane.status");
  const type = watch("lane.type");
  const laneKey = watch("lane.laneKey");
  const mobile = watch("capturePlace.mobile");

  const {data: smartcaptureOptions = [], isLoading: isLoadingSmartcaptureOptions} = useQuery({
    queryFn: () => smartcaptureService.options(),
    queryKey: ["smartcaptureOptions"],
  })

  const {data: archivers = [], isLoading: isLoadingArchivers} = useQuery({
    queryFn: () => nocService.allArchivers(null),
    queryKey: ["archivers"],
    select: data => data.map(el => ({value: el.id, label: el.text}))
  })

  const imageFilteredOptions = useMemo(() => {
    const allowedValues = IMAGE_OPTIONS_RULES[type] || IMAGE_OPTIONS_RULES.DEFAULT
    return imageOptions.filter(option => allowedValues.includes(option.value))
  }, [type]);

  const copyLaneKey = async () => {
    if (!navigator.clipboard) {
      const textArea = document.createElement("textarea");
      textArea.value = laneKey;

      textArea.style.position = "fixed";
      textArea.style.top = "-9999px";
      textArea.style.left = "-9999px";

      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      try {
        document.execCommand("copy"); // remover assim que sair do iframe
        toast.success("LaneKey copiado.");
      } catch (error) {
        toast.error("Erro ao copiar LaneKey.");
      } finally {
        document.body.removeChild(textArea);
      }

      return;
    }

    try {
      await navigator.clipboard.writeText(laneKey);
      toast.success("LaneKey copiado.");
    } catch (error) {
      toast.error("Erro ao copiar LaneKey.");
    }
  };

  const generateLaneKey = async () => {
    try{
      const data = await lanesService.generateLaneKey();
      setValue("lane.laneKey", data, {shouldDirty: true});
      toast.success("LaneKey gerada.")
    }catch(err){
      toast.error("Não foi possível gerar laneKey.")
    }
  }


  const refreshLastPassage = async () => {
    if (!code) return;

    setIsRefreshingLastPassage(true);

    try {
      const latestPassage = await nocService.lastPassageImage(code);

      queryClient.setQueriesData({queryKey: ["dataInfo"]}, old => {
        return mergeLatestPassage(old ?? dataInfo ?? {}, latestPassage);
      });

      queryClient.setQueryData(["laneList"], old => {
        if (!Array.isArray(old)) return old;

        return old.map(item => {
          if (String(item.id ?? item.code) !== String(code)) return item;

          return mergeLatestPassage(item, latestPassage);
        });
      });

      toast.success("Última passagem atualizada.");
    } catch (error) {
      toast.error("Não foi possível atualizar a última passagem.");
    } finally {
      setIsRefreshingLastPassage(false);
    }
  };

  useEffect(() => {
    if(code || archivers.length !== 1 || getValues("lane.archiver")) return;
    setValue("lane.archiver", archivers[0].value, {shouldDirty: true});
  }, [archivers, code, getValues, setValue]);

  const lastPassageImageSrc = dataInfo?.latestPassageUrl
    ? API_URL + "/lanes/latest-passage/get?url=" + dataInfo.latestPassageUrl
    : ""
  return (
    <>
      {isLoadingSmartcaptureOptions && isLoadingArchivers && <Loader/>}
      <Card>
        <Card.Body>
          <SentryRow>
            <div className="col-span-8 grid grid-cols-12 gap-4">
              <div className="col-span-12 flex gap-4">
                <FormField
                  name="lane.status"
                  customRender={field => (
                    <InputSwitch
                      id="laneStatusForm"
                      label={field.value ? "Ativa" : "Inativa"}
                      checked={field.value}
                      disabled={forceInactiveOnCreate}
                      onChange={() => {
                        const newValue = !field.value;
                        if(newValue) {
                          setValue("lane.showlane", false);
                        }
                        field.onChange(newValue)
                      }}
                    />
                  )}
                />
                <div className="flex gap-1 items-center">
                  <FormField
                    name="lane.restricted"
                    customRender={field => (
                      <InputSwitch
                        id="laneRestrictedForm"
                        label="Permissão de acesso especial"
                        checked={field.value}
                        onChange={() => field.onChange(!field.value)}
                      />
                    )}
                  />
                  <div className="rounded-[50%] bg-[#E9ECEF] p-1 flex justify-center items-center" title="A câmera será disponibilizada ao usuário de acordo com a configuração da função">
                    <FaQuestion/>
                  </div>
                </div>
              </div>
              <FormField
                name="lane.description"
                id="laneDescriptionForm"
                as={InputForm}
                label="Nome da câmera"
                classNameWrapper="col-span-12"
              />
              <FormField
                name="lane.type"
                customRender={field => (
                  <SelectComponent
                    instanceId="laneTypeForm"
                    id="laneTypeForm"
                    label="Tipo de dispositivo *"
                    options={laneTypesArr.filter(el => LaneTypeRules[mobile ? "MOBILE" : "DEFAULT"].includes(el.value))}
                    onChange={selected => {
                      field.onChange(handleType(selected));
                    }}
                    isDisabled={code && (type === "MOBILE_CAMERA" || type === "MOBILE_APP")}
                    value={laneTypesArr.find(el => el.value === field.value)}
                    classNameWrapper="col-span-6"
                  />
                )}
              />
              <FormField
                name="lane.smartcapturePin"
                customRender={field => (
                  <SelectComponent
                    instanceId="laneSmartcaptureForm"
                    id="laneSmartcaptureForm"
                    label="Smartcapture"
                    options={smartcaptureOptions}
                    onChange={selected => field.onChange(selected?.value ?? null)}
                    value={smartcaptureOptions.find(el => el.value === field.value) ?? null}
                    isDisabled={!(type === "INTERNAL_SMARTCAPTURE")}
                    classNameWrapper="col-span-6"
                  />
                )}
              />
              <FormField
                name="lane.maintenanceGroupsId"
                customRender={field => (
                  <SelectComponent
                    instanceId="laneMaintenanceGroupForm"
                    id="laneMaintenanceGroupForm"
                    label="Resp. por manutenção"
                    options={maintenanceGroup}
                    isDisabled={(type === "MOBILE_APP" || type === "MOBILE_CAMERA")}
                    onChange={selected => {
                      field.onChange(selected?.value ?? "")
                    }}
                    value={maintenanceGroup.find(el => String(el.value) === String(field.value)) || ""}
                    classNameWrapper="col-span-6"
                  />
                )}
              />
              <FormField
                name="lane.archiver"
                customRender={field => (
                  <SelectComponent
                    instanceId="laneArchiverForm"
                    id="laneArchiverForm"
                    label="Archiver *"
                    options={archivers}
                    isDisabled={code}
                    onChange={selected => field.onChange(selected?.value ?? "")}
                    value={archivers.find(el => el.value === field.value) || ""}
                    classNameWrapper="col-span-6"
                    wrapValues={true}
                  />
                )}
              />
              <FormField
                name="lane.observation"
                id="laneObservationForm"
                label="Observação (NOC)"
                as={InputForm}
                classNameWrapper="col-span-6"
              />
              <FormField
                name="lane.noctime"
                id="laneNocTimeForm"
                label={(type === "MOBILE_APP" || type === "MOBILE_CAMERA") ? "Offline (minutos)" : "Offline (minutos) *"}
                as={InputForm}
                classNameWrapper="col-span-6"
                disabled={(type === "MOBILE_APP" || type === "MOBILE_CAMERA")}
                type="number"
              />
              <FormField
                name="lane.ocr"
                customRender={field => (
                  <SelectComponent
                    instanceId="laneOcrForm"
                    id="laneOcrForm"
                    label="OCR *"
                    options={ocrOptions}
                    onChange={selected => field.onChange(selected?.value)}
                    value={ocrOptions.find(el => el.value === field.value) || ""}
                    classNameWrapper="col-span-6"
                    wrapValues={true}
                  />
                )}
              />
              <FormField
                name="lane.images"
                customRender={field => (
                  <SelectComponent
                    instanceId="laneImages"
                    id="laneImages"
                    label="Imagens *"
                    options={imageFilteredOptions}
                    onChange={selected => field.onChange(selected?.value)}
                    value={imageFilteredOptions.find(el => el.value === field.value) || ""}
                    classNameWrapper="col-span-6"
                    wrapValues={true}
                  />
                )}
              />
              <FormField
                name="lane.rumination"
                customRender={field => (
                  <InputSwitch id="laneRuminationForm"
                               label="Ruminação"
                               className="col-span-4"
                               checked={field.value}
                               disabled={mobile}
                               onChange={() => field.onChange(!field.value)}
                  />
                )}
              />
              <FormField
                name="lane.adr"
                customRender={field => (
                  <InputSwitch id="laneAdrForm"
                               label="Painel de segurança"
                               className="col-span-4"
                               checked={field.value}
                               disabled={mobile}
                               onChange={() => field.onChange(!field.value)}
                  />
                )}
              />
              <FormField
                name="lane.classification"
                customRender={field => (
                  <InputSwitch id="laneClassificationForm"
                               label="Classificação"
                               className="col-span-4"
                               checked={field.value}
                               disabled={mobile}
                               onChange={() => field.onChange(!field.value)}
                  />
                )}
              />
              <FormField
                name="lane.showlane"
                customRender={field => (
                  <InputSwitch id="laneShowLaneForm"
                               label="Exibir na tela de pesquisa do SentryView"
                               className="col-span-6"
                               checked={field.value}
                               onChange={() => field.onChange(!field.value)}
                               disabled={status}
                  />
                )}
              />
              <div className="col-span-12 flex gap-4 w-full">
                <FormField
                  name="lane.laneKey"
                  as={InputForm}
                  id="laneLaneKeyForm"
                  label="LaneKey"
                  classNameWrapper="flex-1"
                  disabled={true}
                />
                <div className="flex gap-2">
                  <Button type="button" title="Copiar laneKey" onClick={copyLaneKey}><FaCopy/></Button>
                  <Button type="button" title="Gerar laneKey" onClick={generateLaneKey} disabled={!code}><FaGears/></Button>
                </div>
              </div>
            </div>
            { (dataInfo?.createdAt || dataInfo?.firstReceptionDate || dataInfo?.latestPassageDatetime) &&
              <div className="col-span-4 grid grid-col-12 gap-2 content-start">
                {lastPassageImageSrc && (
                  <img
                    alt="Última passagem"
                    className="col-span-12"
                    src={lastPassageImageSrc}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = getAssetPath("/img/sentry.imgnotfound.large.748x480.png");
                    }}
                  />
                )}
                <div className="col-span-12">Cadastrado em
                  <span className="font-bold"> {dataInfo?.createdAt ? projectUtils.formatDate(dataInfo.createdAt, "DD/MM/YYYY hh:mm:ss") : ""}</span>
                </div>
                <div className="col-span-12">Primeira passagem em
                  <span className="font-bold"> {dataInfo?.firstReceptionDate ? projectUtils.formatDate(dataInfo.firstReceptionDate, "DD/MM/YYYY hh:mm:ss") : ""}</span>
                </div>
                <div className="col-span-12">Última passagem em
                  <span className="font-bold"> {dataInfo?.latestPassageDatetime ? projectUtils.formatDate(dataInfo.latestPassageDatetime, "DD/MM/YYYY hh:mm:ss") : ""}</span>
                </div>
                <div className="col-span-12 flex justify-start">
                    <Button
                      type="button"
                      target="primary-outline"
                      title="Atualizar última passagem"
                      onClick={refreshLastPassage}
                      loading={isRefreshingLastPassage}
                      loadingText="Atualizando..."
                      disabled={!code}
                    >
                      <FaRotateRight/> Atualizar última passagem
                    </Button>
                  </div>
              </div>
            }
          </SentryRow>
        </Card.Body>
      </Card>
    </>
  )
}

export default LaneForm;
